import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useTheme } from '../ThemeContext';
import './Navbar.css';

const navItems = [
  { label: 'Home', path: '/', icon: '🏠' },
  {
    label: 'Kubernetes',
    path: '/kubernetes',
    icon: '⚙️',
    submenu: [
      { label: 'Cluster Upgrade', path: '/kubernetes/cluster-upgrade', icon: '⬆️' },
      { label: 'Security',        path: '/kubernetes/security',        icon: '🔐' },
      { label: 'TLS in K8s',      path: '/kubernetes/tls',             icon: '🪪' },
      {
        label: 'K8s Components',
        icon: '🧩',
        submenu: [
          { label: 'Controller', path: '/kubernetes/components/controller', icon: '🔁' },
        ],
      },
    ],
  },
  {
    label: 'OpenShift',
    path: '/openshift',
    icon: '🔴',
    submenu: [
      { label: 'Overview', path: '/openshift/overview', icon: '🗺️' },
      { label: 'Storage', path: '/openshift/storage', icon: '💾' },
      { label: 'Workloads', path: '/openshift/workloads', icon: '🚀' },
      { label: 'SecurityContext (SCC)', path: '/openshift/scc', icon: '🛡️' },
    ],
  },
  {
    label: 'Python',
    path: '/python',
    icon: '🐍',
    submenu: [
      { label: 'Inheritance', path: '/python/inheritance', icon: '🧬' },
      {
        label: 'File Handling',
        icon: '📁',
        submenu: [
          { label: 'Practice Questions', path: '/python/test', icon: '📝' },
          { label: 'JSON',               path: '/python/json', icon: '🧾' },
        ],
      },
      { label: 'Pandas', path: '/python/pandas', icon: '🐼' },
      { label: 'Pydantic', path: '/python/pydantic', icon: '🔷' },
      {
        label: 'Threading',
        icon: '🧵',
        submenu: [
          { label: 'Threading',                     path: '/python/threading',                      icon: '🧵' },
          { label: 'Threading vs MultiProcessing',  path: '/python/threading-vs-multiprocessing',  icon: '⚙️' },
          { label: 'Exercises',                     path: '/python/threading-exercises',            icon: '📝' },
          { label: 'Exercises - 2',                path: '/python/threading-exercises-2',          icon: '📗' },
          { label: 'ThreadPool & Futures',          path: '/python/threadpool-futures',             icon: '🔔' },
          { label: 'Exercises - 3 (Threadpool)',   path: '/python/threading-exercises-3',          icon: '📦' },
        ],
      },
    ],
  },
  {
    label: 'AWS',
    path: '/aws',
    icon: '☁️',
    submenu: [
      { label: 'AWS Overview', path: '/aws/overview', icon: '🗺️' },
      { label: 'Networking', path: '/aws/networking', icon: '🔌' },
    ],
  },
  {
    label: 'Database',
    path: '/database',
    icon: '🗄️',
    submenu: [
      { label: 'DB Commands',       path: '/database/db-commands',      icon: '📋' },
      { label: 'DB Zero to Hero',   path: '/database/zero-to-hero',     icon: '🗄️' },
    ],
  },
  {
    label: 'AI',
    path: '/ai',
    icon: '🤖',
    submenu: [
      { label: 'Agentic AI & ADK', path: '/ai/agentic-adk', icon: '🧠' },
    ],
  },
  {
    label: 'Academics',
    path: '/academics',
    icon: '🎓',
    submenu: [
      {
        label: 'Class 9',
        icon: '📚',
        submenu: [
          {
            label: 'Maths',
            icon: '➗',
            submenu: [
              { label: 'Maths SQP-I', path: '/academics/class9/maths/sqp1', icon: '📄' },
              { label: 'Chapter 1',   path: '/academics/class9/maths/ch1',  icon: '📐' },
              { label: 'Chapter 2',   path: '/academics/class9/maths/ch2',  icon: '📈' },
            ],
          },
          { label: 'Science',        path: '/academics/class9/science', icon: '🔬' },
          { label: 'Social Science', path: '/academics/class9/sst',     icon: '🌍' },
          { label: 'Hindi',          path: '/academics/class9/hindi',   icon: '🔤' },
          { label: 'English',        path: '/academics/class9/english', icon: '📖' },
        ],
      },
    ],
  },
  {
    label: 'System Design',
    path: '/system-design',
    icon: '🏗️',
    submenu: [
      { label: 'Load Balancer', path: '/system-design/load-balancer', icon: '⚖️' },
    ],
  },
];

function isSubmenuActive(submenu, pathname) {
  return submenu?.some(
    (s) => s.path === pathname || (s.submenu && isSubmenuActive(s.submenu, pathname))
  );
}

function SubmenuItem({ item, location, closeMenu }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!item.submenu) {
    return (
      <li>
        <Link
          to={item.path}
          className={`submenu-link ${location.pathname === item.path ? 'active' : ''}`}
          onClick={closeMenu}
        >
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </Link>
      </li>
    );
  }

  const isActive = isSubmenuActive(item.submenu, location.pathname);

  return (
    <li className="submenu-nested" ref={ref}>
      <div className="nav-link-group">
        <span className={`submenu-link submenu-parent ${isActive ? 'active' : ''}`}>
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </span>
        <button
          className={`dropdown-toggle nested ${open ? 'open' : ''}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle submenu"
        >
          ▸
        </button>
      </div>

      <ul className={`submenu submenu-flyout ${open ? 'open' : ''}`}>
        {item.submenu.map((sub) =>
          sub.submenu ? (
            <SubmenuItem
              key={sub.label}
              item={sub}
              location={location}
              closeMenu={() => { setOpen(false); closeMenu(); }}
            />
          ) : (
            <li key={sub.path}>
              <Link
                to={sub.path}
                className={`submenu-link ${location.pathname === sub.path ? 'active' : ''}`}
                onClick={() => { setOpen(false); closeMenu(); }}
              >
                <span className="nav-icon">{sub.icon}</span>
                <span>{sub.label}</span>
              </Link>
            </li>
          )
        )}
      </ul>
    </li>
  );
}

function NavItem({ item, location, closeMenu }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive =
    location.pathname === item.path || isSubmenuActive(item.submenu, location.pathname);

  if (!item.submenu) {
    return (
      <li>
        <Link
          to={item.path}
          className={`nav-link ${isActive ? 'active' : ''}`}
          onClick={closeMenu}
        >
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </Link>
      </li>
    );
  }

  return (
    <li className="has-submenu" ref={ref}>
      <div className="nav-link-group">
        <Link
          to={item.path}
          className={`nav-link ${isActive ? 'active' : ''}`}
          onClick={closeMenu}
        >
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </Link>
        <button
          className={`dropdown-toggle ${dropdownOpen ? 'open' : ''}`}
          onClick={() => setDropdownOpen((o) => !o)}
          aria-label="Toggle submenu"
        >
          ▾
        </button>
      </div>

      <ul className={`submenu ${dropdownOpen ? 'open' : ''}`}>
        {item.submenu.map((sub) =>
          sub.submenu ? (
            <SubmenuItem
              key={sub.label}
              item={sub}
              location={location}
              closeMenu={() => { setDropdownOpen(false); closeMenu(); }}
            />
          ) : (
            <li key={sub.path}>
              <Link
                to={sub.path}
                className={`submenu-link ${location.pathname === sub.path ? 'active' : ''}`}
                onClick={() => { setDropdownOpen(false); closeMenu(); }}
              >
                <span className="nav-icon">{sub.icon}</span>
                <span>{sub.label}</span>
              </Link>
            </li>
          )
        )}
      </ul>
    </li>
  );
}

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  return (
    <nav className="navbar">
      <Link to="/" className="navbar-brand" onClick={() => setMenuOpen(false)}>
        <Logo size={40} className="brand-logo" />
        <span className="brand-text">
          <span className="brand-name">
            <span className="brand-name-light">Br</span><span className="brand-name-ai">AI</span><span className="brand-name-light">n</span><span className="brand-name-blue">Cloud</span>
          </span>
          <span className="brand-tagline">Learn · Practice · Grow</span>
        </span>
      </Link>

      <button
        className={`hamburger ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            location={location}
            closeMenu={() => setMenuOpen(false)}
          />
        ))}
      </ul>

      <button
        className="theme-toggle"
        onClick={toggleTheme}
        aria-label="Toggle dark/light theme"
        title="Toggle theme"
      >
        <span className="theme-toggle-dot dot-moon">●</span>
        <span className="theme-toggle-dot dot-sun">○</span>
        <span className={`theme-toggle-knob ${theme === 'light' ? 'knob-light' : ''}`} />
      </button>
    </nav>
  );
}

export default Navbar;
